// src/pages/Workforce.tsx
// ─────────────────────────────────────────────────────────────────
// WORKFORCE — consolidated page replacing Crew Roster + Employees + Timesheets
// Implements the Workforce Module Consolidation Review (9 July 2026)
//
// Tab structure:
//   [Roster & Allocation]  → full Roster.tsx (allocation grid, equipment,
//                            payroll summary, change history)
//   [Timesheets]           → full Timesheets.tsx (entry, approval, payslips,
//                            resource forecast, TOIL)
//   [Employees]            → full Employees.tsx (register, credentials,
//                            leave, apprenticeship)
//   [Payroll overview]     → new combined estimated-vs-actual view
//
// Mobile & Crew (MobileComms.tsx) is unchanged — still the field-facing hub.
// Roster.tsx is UNCHANGED — MobileComms still imports its shared store functions.
//
// Shift parameters (Section 6.3):
//   DAY shift  = Mon–Fri (days 1–5)   DAY_SHIFT_WORK_DAYS  = [1,2,3,4,5]
//   NIGHT shift = Sun–Thu (days 0–4)  NIGHT_SHIFT_WORK_DAYS = [0,1,2,3,4]
//   BOTH view  = Sun–Sat (7 columns, Sunday leftmost)
// ─────────────────────────────────────────────────────────────────
import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import {
  Users, Calendar, ClipboardList, DollarSign, LayoutDashboard,
} from 'lucide-react'

// Existing page components — rendered as tab content
import { Roster, getRosterAllocations } from './Roster'
import { Timesheets }                    from './Timesheets'
import { Employees }                     from './Employees'
import {
  MOCK_EMPLOYEES, PAY_RATES, WEEK_DATES, MOCK_TIMESHEETS,
} from '../utils/mockDataExtended'
import { generatePayrollSummary } from '../utils/mockDataRoster'
import { formatCurrency }          from '../utils/formatters'

// ─────────────────────────────────────────────────────────────────
// PAYROLL OVERVIEW TAB
// Section 6.1: combined estimated (roster) vs actual (approved timesheets)
// ─────────────────────────────────────────────────────────────────
function WorkforcePayrollOverview() {
  const allocations = getRosterAllocations()
  const empNameMap  = Object.fromEntries(MOCK_EMPLOYEES.map((e: any) => [e.id, e.fullName]))
  const rosterLines = generatePayrollSummary(WEEK_DATES, allocations, empNameMap)

  // Roster: estimated hours by employee
  const estByEmp: Record<string, number> = {}
  for (const l of rosterLines) {
    estByEmp[l.employeeId] = (estByEmp[l.employeeId] ?? 0) + l.hoursWorked
  }

  // Timesheets: actual approved hours + gross
  const actByEmp: Record<string, { ordinary: number; overtime: number; gross: number }> = {}
  for (const ts of MOCK_TIMESHEETS as any[]) {
    if (ts.status !== 'APPROVED' && ts.status !== 'PROCESSED') continue
    const emp  = MOCK_EMPLOYEES.find((e: any) => e.id === ts.employeeId) as any
    const rate = PAY_RATES[emp?.payClassification ?? ''] ?? 0
    let ord = 0, ot = 0
    for (const entry of ts.entries ?? []) {
      if (!entry.start || !entry.end) continue
      const [sh, sm] = (entry.start as string).split(':').map(Number)
      const [eh, em] = (entry.end   as string).split(':').map(Number)
      const raw = Math.max(0, (eh * 60 + em - sh * 60 - sm - (entry.break ?? 0)) / 60)
      ord += Math.min(raw, 8)
      ot  += Math.max(raw - 8, 0)
    }
    if (!actByEmp[ts.employeeId]) actByEmp[ts.employeeId] = { ordinary: 0, overtime: 0, gross: 0 }
    actByEmp[ts.employeeId].ordinary += ord
    actByEmp[ts.employeeId].overtime  += ot
    actByEmp[ts.employeeId].gross    += ord * rate + ot * rate * 1.5
  }

  const empIds = [...new Set([...Object.keys(estByEmp), ...Object.keys(actByEmp)])]

  if (empIds.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl py-14 text-center text-gray-400">
        <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">No roster or timesheet data for the current week.</p>
        <p className="text-xs mt-1">Build the week roster in the Roster & Allocation tab, then approve timesheets here.</p>
      </div>
    )
  }

  const totalEst   = Object.values(estByEmp).reduce((s, h) => s + h, 0)
  const totalActH  = Object.values(actByEmp).reduce((s, a) => s + a.ordinary + a.overtime, 0)
  const totalGross = Object.values(actByEmp).reduce((s, a) => s + a.gross, 0)

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800">
        <strong>Estimated vs actual.</strong> Estimated hours come from roster allocations (planned).
        Actual hours come from approved timesheets. Variances above 1h are flagged for review.
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 bg-gray-50 border-b">
                <th className="text-left px-4 py-3 font-medium">Employee</th>
                <th className="text-right px-3 py-3 font-medium">Est. hrs</th>
                <th className="text-right px-3 py-3 font-medium">Actual hrs</th>
                <th className="text-right px-3 py-3 font-medium">Variance</th>
                <th className="text-right px-3 py-3 font-medium">OT hrs</th>
                <th className="text-right px-3 py-3 font-medium">Actual gross est.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {empIds.map(id => {
                const emp      = MOCK_EMPLOYEES.find((e: any) => e.id === id) as any
                const estH     = estByEmp[id] ?? 0
                const actual   = actByEmp[id]
                const actH     = actual ? actual.ordinary + actual.overtime : 0
                const variance = +(actH - estH).toFixed(1)
                const hasFlag  = Math.abs(variance) > 1 && estH > 0
                return (
                  <tr key={id} className={hasFlag ? 'bg-amber-50/40' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm text-gray-900">{emp?.fullName ?? id}</div>
                      <div className="text-xs text-gray-400">{(emp?.payClassification ?? '').replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-600">{estH > 0 ? `${estH.toFixed(1)}h` : '—'}</td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium text-gray-900">{actH > 0 ? `${actH.toFixed(1)}h` : '—'}</td>
                    <td className={`px-3 py-3 text-right tabular-nums font-semibold ${hasFlag ? (variance > 0 ? 'text-amber-600' : 'text-red-600') : actH > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                      {actH > 0 ? `${variance > 0 ? '+' : ''}${variance}h` : '—'}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-gray-600">
                      {actual?.overtime ? `${actual.overtime.toFixed(1)}h` : '—'}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {actual?.gross ? (
                        <span className="font-semibold text-gray-900">{formatCurrency(actual.gross)}</span>
                      ) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50">
                <td className="px-4 py-3 font-semibold text-sm text-gray-900">Totals</td>
                <td className="px-3 py-3 text-right font-bold tabular-nums">{totalEst.toFixed(1)}h</td>
                <td className="px-3 py-3 text-right font-bold tabular-nums">{totalActH.toFixed(1)}h</td>
                <td colSpan={2} />
                <td className="px-3 py-3 text-right font-bold tabular-nums">{formatCurrency(totalGross)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-400">
        Gross estimates use ordinary and overtime rates only. Use Timesheets → Payslips for the full breakdown including night loading and allowances.
      </p>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// WORKFORCE PAGE
// ─────────────────────────────────────────────────────────────────
type WorkforceTab = 'roster' | 'timesheets' | 'employees' | 'payroll'

export function Workforce() {
  const { user } = useAuthStore()
  const [tab, setTab] = useState<WorkforceTab>('roster')

  if (!user) return null

  const isFieldCrew = user?.tier === 'D_FIELD'  // isTier('D_FIELD') is always true for all users — check tier directly
  // D_FIELD → timesheets only (their own hours)
  const effectiveTab: WorkforceTab = isFieldCrew ? 'timesheets' : tab

  const ALL_TABS: { key: WorkforceTab; icon: any; label: string }[] = [
    { key: 'roster',     icon: Calendar,      label: 'Roster & Allocation' },
    { key: 'timesheets', icon: ClipboardList,  label: 'Timesheets' },
    { key: 'employees',  icon: Users,          label: 'Employees' },
    { key: 'payroll',    icon: DollarSign,     label: 'Payroll overview' },
  ]

  const visibleTabs = isFieldCrew
    ? ALL_TABS.filter(t => t.key === 'timesheets')
    : ALL_TABS

  return (
    <div className="space-y-5">

      {/* Page header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-brand-navy flex items-center justify-center flex-shrink-0">
          <LayoutDashboard className="w-4 h-4 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workforce</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {isFieldCrew
              ? 'Your timesheets and work records'
              : 'Roster planning · Timesheets · Employees · Payroll — consolidated'}
          </p>
        </div>
      </div>

      {/* Tab bar — pill style matching the rest of the platform */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
        {visibleTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              effectiveTab === t.key
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Roster & Allocation ────────────────────────────────────
           Renders the full Roster component.
           Sub-tabs within Roster: Crew roster | Equipment | Payroll | History
           This is where all crew and equipment allocation is done.
      ────────────────────────────────────────────────────────────── */}
      {effectiveTab === 'roster' && <Roster />}

      {/* ── Timesheets ─────────────────────────────────────────────
           Renders the full Timesheets component.
           Sub-views: Timesheets | Reconciliation | Payslips | Resource forecast
      ────────────────────────────────────────────────────────────── */}
      {effectiveTab === 'timesheets' && <Timesheets />}

      {/* ── Employees ──────────────────────────────────────────────
           Renders the full Employees component.
           Includes: register, credentials, leave balances, apprenticeship
      ────────────────────────────────────────────────────────────── */}
      {effectiveTab === 'employees' && <Employees />}

      {/* ── Payroll overview ───────────────────────────────────────
           Combined estimated vs actual payroll comparison
      ────────────────────────────────────────────────────────────── */}
      {effectiveTab === 'payroll' && (
        <div className="space-y-3">
          <div>
            <h2 className="font-bold text-gray-900">Payroll overview</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Week of {WEEK_DATES[0]} — roster allocations vs approved timesheet hours
            </p>
          </div>
          <WorkforcePayrollOverview />
        </div>
      )}

    </div>
  )
}
